import { doc, getDoc, setDoc, updateDoc, increment, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebaseConfig.js";

/**
 * Initializes the stats/usage document in Firestore with default values.
 * Call this once to create the document if it doesn't exist.
 * 
 * @returns {Promise<void>}
 */
export async function initializeStats() {
  try {
    const statsRef = doc(db, "stats", "usage");
    const snap = await getDoc(statsRef);

    if (!snap.exists()) {
      await setDoc(statsRef, {
        totalCreators: 0,
        lastUpdated: serverTimestamp(),
      });
      console.log("✅ Stats document initialized");
    } else {
      console.log("ℹ️ Stats document already exists");
    }
  } catch (error) {
    console.error("Error initializing stats:", error);
    throw error;
  }
}

/**
 * Atomically increments the total creator count in Firestore.
 * This function is safe to call concurrently - it uses Firestore's increment()
 * operator to handle race conditions.
 * 
 * The stats document is automatically created if it doesn't exist.
 * 
 * @returns {Promise<void>}
 */
export async function incrementCreatorCount() {
  try {
    console.log("📊 [incrementCreatorCount] Starting...");
    const statsRef = doc(db, "stats", "usage");
    const snap = await getDoc(statsRef);
    console.log("📊 [incrementCreatorCount] Document exists?", snap.exists());

    if (!snap.exists()) {
      // Create doc if missing
      console.log("📊 [incrementCreatorCount] Creating new stats document with totalCreators: 1");
      await setDoc(statsRef, {
        totalCreators: 1,
        lastUpdated: serverTimestamp(),
      });
      console.log("✅ [incrementCreatorCount] Stats document created successfully");
    } else {
      // Atomic increment to avoid race conditions
      const currentCount = snap.data()?.totalCreators || 0;
      console.log(`📊 [incrementCreatorCount] Incrementing from ${currentCount} to ${currentCount + 1}`);
      await updateDoc(statsRef, {
        totalCreators: increment(1),
        lastUpdated: serverTimestamp(),
      });
      console.log("✅ [incrementCreatorCount] Stats document updated successfully");
    }
  } catch (error) {
    console.error("❌ [incrementCreatorCount] Error incrementing creator count:", error);
    console.error("❌ [incrementCreatorCount] Error details:", {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    // Don't throw - we don't want to break signup if stats fail
  }
}

